<template>
  <content-container title="服务商合同" :table-row-count="tableData.length">
    <!-- 搜索条件区域 -->
    <search-container>
      <search-col>
        <el-input v-model="params.serverNo" placeholder="服务商编号" clearable @keydown="onEnter" />
      </search-col>
      <search-col>
        <el-select
          v-model="params.serverId"
          filterable
          placeholder="服务商名称"
          clearable
        >
          <el-option
            v-for="item in optionsServer"
            :key="item.id"
            :label="item.serverName"
            :value="item.id"
          />
        </el-select>
      </search-col>
      <search-col>
        <el-select
          v-model="params.businessType"
          filterable
          placeholder="业务线"
          clearable
          @keyup.enter="doSearch"
        >
          <el-option :label="freelancerDutyTypeLabel[EFreelancerDutyType.CROWD]" :value="EFreelancerDutyType.CROWD" />
          <el-option :label="freelancerDutyTypeLabel[EFreelancerDutyType.MATCH]" :value="EFreelancerDutyType.MATCH" />
        </el-select>
      </search-col>
      <search-col>
        <el-select
          v-model="params.proxyPayType"
          filterable
          placeholder="收入性质"
          clearable
        >
          <el-option :label="proxyPayTypeConfig[EProxyPayType.BUSINESS_INCOME]" :value="EProxyPayType.BUSINESS_INCOME" />
          <el-option :label="proxyPayTypeConfig[EProxyPayType.LABOR_REMUNERATION]" :value="EProxyPayType.LABOR_REMUNERATION" />
        </el-select>
      </search-col>
      <search-col>
        <el-select
          v-model="params.invoiceTaxMethod"
          filterable
          placeholder="开票方式"
          clearable
        >
          <el-option :label="invoiceTaxMethodConfig[EInvoiceTaxMethod.FULL_AMOUNT_TAX]" :value="EInvoiceTaxMethod.FULL_AMOUNT_TAX" />
          <el-option :label="invoiceTaxMethodConfig[EInvoiceTaxMethod.DIFFERENTIAL_TAX]" :value="EInvoiceTaxMethod.DIFFERENTIAL_TAX" />
        </el-select>
      </search-col>
      <search-col>
        <el-select
          v-model="params.contractTempId"
          filterable
          placeholder="合同模版名称"
          clearable
        >
          <el-option
            v-for="item in dutyContractConfigDTOOptions"
            :key="item.id"
            :label="item.tempName"
            :value="item.id"
          />
        </el-select>
      </search-col>
      <template #buttons>
        <el-button @click="resetForm">
          <svg-icon icon="tabler:refresh" />重置
        </el-button>
        <el-button type="primary" @click="doSearch">
          <svg-icon icon="lucide:search" />查询
        </el-button>
      </template>
    </search-container>

    <!-- 非搜索区域的额外按钮 -->
    <div>
      <el-button type="primary" class="mb-[20px]" @click="exportTable">
        <svg-icon icon="mingcute:file-export-line" />导出
      </el-button>
    </div>

    <!-- 表格数据 -->
    <el-table v-loading="loading" class="normal-table" :border="true" :scrollbar-always-on="true" :data="tableData" :cell-style="initOperatorColumnStyle" tooltip-effect="dark nhb-max-w-500">
      <el-table-column align="left" prop="serverNo" label="服务商编号" :width="140" show-overflow-tooltip />
      <el-table-column align="left" prop="serverName" label="服务商名称" :min-width="140" show-overflow-tooltip />
      <el-table-column align="left" prop="businessType" label="业务线" :width="120">
        <template #default="scope">
          {{ freelancerDutyTypeLabel[scope.row.businessType] || '-' }}
        </template>
      </el-table-column>
      <el-table-column align="left" prop="proxyPayType" label="收入性质" :width="140">
        <template #default="scope">
          {{ scope.row.proxyPayType ? proxyPayTypeConfig[scope.row.proxyPayType] : '-' }}
        </template>
      </el-table-column>
      <el-table-column align="left" prop="invoiceTaxMethod" label="开票方式" :width="100">
        <template #default="scope">
          {{ scope.row.invoiceTaxMethod ? invoiceTaxMethodConfig[scope.row.invoiceTaxMethod] : '-' }}
        </template>
      </el-table-column>
      <el-table-column align="left" prop="fddTempId" label="模版ID" :min-width="140" show-overflow-tooltip />
      <el-table-column align="left" prop="contractTempName" label="服务商合同模版" :min-width="240">
        <template #default="scope">
          <div class="flex justify-start items-center">
            <el-tag v-if="scope.row.defaultTemp" type="success" class="mr-2">
              默认模版
            </el-tag>
            <el-tooltip
              v-if="scope.row.contractTempName"
              effect="light"
              :content="scope.row.contractTempName"
              placement="top"
            >
              <el-link type="primary" :underline="false" @click="toPreview(scope.row)">
                {{ scope.row.contractTempName ? scope.row.contractTempName : '--' }}
              </el-link>
            </el-tooltip>
            <span v-else> -- </span>
          </div>
        </template>
      </el-table-column>
      <el-table-column
        v-if="buttons.length > 0"
        align="center"
        prop="actions"
        fixed="right"
        label="操作"
        width="150px"
      >
        <template #default="scope">
          <table-row-buttons :buttons="buttons" :row="scope.row" />
        </template>
      </el-table-column>
    </el-table>

    <!-- 底部分页 -->
    <template #footer>
      <el-pagination
        v-model:current-page="params.page"
        v-model:page-size="params.size"
        :page-sizes="[defaultPageSize, 25, 50, 100]"
        layout="total, sizes, prev, pager, next, jumper"
        :total="total"
        background
        @size-change="onSizeChange"
        @current-change="onCurrentPageChange"
      />
    </template>

    <!-- 配置 -->
    <select-contract-temp
      :visible="showSelectContractTemp"
      :row="currentRow"
      @close="showSelectContractTemp = false"
      @confirm="handleSelectContractTempConfirm"
    />
  </content-container>
</template>

<script setup lang="ts">
import { downloadFile } from '@core/tools';
import { reactive, ref, PropType } from 'vue';
import { ElMessage } from 'element-plus';
import { SvgIcon, TableRowButtons, initOperatorColumnStyle } from '@vue3/components';
import { 
  ContentContainer, 
  SearchContainer, 
  SearchCol, 
  useTblPermissionButtons, 
  queryContractTempPreviewTempUrl, 
  queryServerList, 
  queryServerContractTemp,
  exportServerContractTemp,
  queryServerContractTempGetTemp,
  queryServerContractTempEnable,
  queryContractTempAvailableTemp, 
} from '@core/main';
import { 
  IContractTempDO,
  IServerOptionsItem,
  freelancerDutyTypeLabel, 
  EFreelancerDutyType, 
  proxyPayTypeConfig, 
  EProxyPayType,
  IServerContractTempItem,
  IServerContractTempParams,
  ETempBusiness, 
  invoiceTaxMethodConfig,
  EInvoiceTaxMethod,
} from '@core/main/types';
import SelectContractTemp from './dialog/SelectContractTemp.vue';

defineOptions({ name: 'ServiceContract' });

defineProps({
  from: {
    type: String as PropType<'saas' | 'company' | 'agent' | 'server'>,
    default: 'saas',
  },
});

const defaultPageSize = 10;
const params = reactive<IServerContractTempParams>({
  page: 1,
  size: defaultPageSize,
});

const loading = ref(false);
const total = ref(0);
const tableData = ref<IServerContractTempItem[]>([] as IServerContractTempItem[]);

const dutyContractConfigDTOOptions = ref([] as IContractTempDO[]);
const optionsServer = ref<IServerOptionsItem[]>([]);

const showSelectContractTemp = ref(false);
const currentRow = ref<IServerContractTempItem>({} as IServerContractTempItem);

const { buttons } = useTblPermissionButtons([
  {
    name: '配置',
    code: '',
    action: toConfigure,
  },
  {
    name: '启用默认模版',
    code: '',
    relationField: 'defaultTemp',
    relationValue: 0,
    action: useDefault,
  },
]);

async function toConfigure(row: any) {
  currentRow.value = row;
  showSelectContractTemp.value = true;
}

async function handleSelectContractTempConfirm(val: any) {
  showSelectContractTemp.value = false;
  try {
    await queryServerContractTempGetTemp({
      contractTempId: val,
      id: Number(currentRow.value.id),
    });
    fetchData();
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }
}

async function useDefault(row: IServerContractTempItem) {
  try {
    await queryServerContractTempEnable(Number(row.id));
    
    fetchData();
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }
}

async function exportTable() {
  try {
    const { buffer, fileName } = await exportServerContractTemp(params);

    downloadFile(buffer, fileName, 'xlsx');
    ElMessage.success('导出成功');
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }
}

async function toPreview(row) {
  try {
    const res = await queryContractTempPreviewTempUrl(row.contractTempId);

    window.open(res);
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }
}

async function fetchData() {
  loading.value = true;

  try {
    const { data, total: all } = await queryServerContractTemp({ ...params });

    tableData.value = data;
    total.value = all;
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }

  loading.value = false;
}

function resetForm() {
  for (const key in params) {
    if (!['page', 'size'].includes(key)) {
      params[key] = '';
    }
  }
}

function onSizeChange(size: number) {
  params.size = size;
  params.page = 1;
  fetchData();
}

function onCurrentPageChange(page: number) {
  params.page = page;
  fetchData();
}

function doSearch() {
  params.page = 1;
  fetchData();
}

function onEnter(e) {
  if (e.keyCode === 13) doSearch();
}

async function fetchTastContractTempOptions() {
  try {
    const res = await queryContractTempAvailableTemp([
      ETempBusiness.FREELANCER_CONTRACT, 
      ETempBusiness.BROKER_COLLECTION_CONTRACT,
    ]);

    dutyContractConfigDTOOptions.value = res;
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }
}
async function getServerList() {
  try {
    const { data } = await queryServerList({});

    optionsServer.value = data;
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }
}

async function init() {
  await fetchData();
  await fetchTastContractTempOptions();
  await getServerList();
}

init();

</script>
